// The planet's ground truth: a deterministic heightfield evaluated
// identically by the server (collision, hover height, spawn placement) and
// the client (terrain meshes) — "what you see is exactly what you collide
// with", extended to 16.4 km. No heightmap asset, no server mesh: the
// function IS the terrain.
//
// Determinism is a wire-protocol-grade obligation here, so this module uses
// only operations IEEE 754 guarantees to be exactly rounded and that JS
// therefore evaluates bit-identically in every engine: + - * /, sqrt,
// floor, abs, min/max, and integer ops. Math.sin/cos/pow/exp are NOT in
// that set (their last-bit rounding is engine-defined) and must never
// appear below. The golden-value test locks this in.

/** Planet edge length in metres (2^14 — SWG scale, power-of-two chunks). */
export const PLANET_SIZE = 16384;
/** Coordinates run −PLANET_HALF..+PLANET_HALF on x and z. */
export const PLANET_HALF = PLANET_SIZE / 2;

/** Inside this radius of the origin the settlement plateau is dead flat. */
export const SETTLEMENT_FLAT_RADIUS = 250;
/** By this radius the plateau has fully blended into open terrain. */
export const SETTLEMENT_BLEND_RADIUS = 600;
/** The plateau's elevation. Zero keeps every existing street/club mesh valid. */
export const SETTLEMENT_PLATEAU_Y = 0;

/**
 * The planet's single seed. Changing it is terraforming: every hill, biome
 * band, and prop placement moves. Persisted positions would survive, but
 * anything placed "on that ridge" stops making sense — treat as immutable
 * once characters roam.
 */
export const TERRAIN_SEED = 0x5eba11;

export const BIOMES = ["settlement", "plains", "ridgeland", "mesa", "fen"] as const;
export type Biome = (typeof BIOMES)[number];

// ---------------------------------------------------------------------------
// Deterministic noise primitives. Exported for reuse by anything that needs
// seeded, position-stable randomness (prop scatter, resource concentration
// fields); they are generic — nothing secret lives in the functions, only
// in the seeds callers feed them.

/** Avalanche an (seed, xi, zi) integer triple into a uint32. */
export function hash2(seed: number, xi: number, zi: number): number {
  let h = (seed | 0) ^ Math.imul(xi | 0, 0x27d4eb2d) ^ Math.imul(zi | 0, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
}

/** Uniform [0, 1) from an integer lattice point. */
export function rand2(seed: number, xi: number, zi: number): number {
  return hash2(seed, xi, zi) / 4294967296;
}

/** Quintic fade (C2 continuity, so terrain normals stay smooth). */
function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Value noise in [-1, 1] at frequency 1 (callers scale coordinates).
 * Bilinear quintic interpolation of hashed lattice corners.
 */
export function valueNoise2(seed: number, x: number, z: number): number {
  const xi = Math.floor(x);
  const zi = Math.floor(z);
  const fx = x - xi;
  const fz = z - zi;
  const ux = fade(fx);
  const uz = fade(fz);
  const v00 = rand2(seed, xi, zi);
  const v10 = rand2(seed, xi + 1, zi);
  const v01 = rand2(seed, xi, zi + 1);
  const v11 = rand2(seed, xi + 1, zi + 1);
  return (lerp(lerp(v00, v10, ux), lerp(v01, v11, ux), uz) - 0.5) * 2;
}

/** Fractal Brownian motion of valueNoise2, normalized to [-1, 1]. */
export function fbm2(seed: number, x: number, z: number, octaves: number): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let fx = x;
  let fz = z;
  for (let i = 0; i < octaves; i++) {
    sum += valueNoise2(seed + i * 1013, fx, fz) * amp;
    norm += amp;
    amp *= 0.5;
    fx *= 2;
    fz *= 2;
  }
  return sum / norm;
}

/** Ridged fbm in [0, 1]: sharp crests where the noise crosses zero. */
function ridgedFbm2(seed: number, x: number, z: number, octaves: number): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let fx = x;
  let fz = z;
  for (let i = 0; i < octaves; i++) {
    sum += (1 - Math.abs(valueNoise2(seed + i * 2027, fx, fz))) * amp;
    norm += amp;
    amp *= 0.5;
    fx *= 2;
    fz *= 2;
  }
  return sum / norm;
}

/** 0 below `lo`, 1 above `hi`, smooth (C1) in between. */
export function smoothstep(lo: number, hi: number, v: number): number {
  const t = Math.min(1, Math.max(0, (v - lo) / (hi - lo)));
  return t * t * (3 - 2 * t);
}

// ---------------------------------------------------------------------------
// The heightfield. Layers:
//   1. domain warp — bends every later lookup so nothing reads as a grid
//   2. biome banding — a slow selector noise weights four terrain characters
//   3. settlement mask — flattens the origin plateau the street sits on

// Derived sub-seeds; spacing is arbitrary, stability is what matters.
const SEED_WARP_X = TERRAIN_SEED + 101;
const SEED_WARP_Z = TERRAIN_SEED + 202;
const SEED_BASE = TERRAIN_SEED + 303;
const SEED_RIDGE = TERRAIN_SEED + 404;
const SEED_BAND = TERRAIN_SEED + 505;

/** Metres of lateral distortion applied before terrain lookups. */
const WARP_AMPLITUDE = 220;
const WARP_FREQ = 1 / 1400;
/** Rolling-hills base frequency (~700 m features). */
const BASE_FREQ = 1 / 700;
/** Ridge crest frequency (~1.1 km spacing between major crests). */
const RIDGE_FREQ = 1 / 1100;
/** Biome bands drift over ~4 km — big enough to walk INTO a biome. */
const BAND_FREQ = 1 / 4000;

/** Height contribution of each biome character at a (warped) point. */
function fenHeight(base: number): number {
  return base * 3 - 4;
}
function plainsHeight(base: number): number {
  return base * 11 + 4;
}
function ridgelandHeight(base: number, ridge: number): number {
  return base * 12 + ridge * 78 + 6;
}
function mesaHeight(base: number): number {
  // Terraced steps: quantize the rolling base into benches, then blend a
  // little of the smooth signal back so the risers aren't perfectly sheer.
  const t = base * 0.5 + 0.5;
  const stepped = Math.floor(t * 5) / 5;
  return (stepped * 0.72 + t * 0.28) * 110 + 8;
}

export interface BandWeights {
  fen: number;
  plains: number;
  ridgeland: number;
  mesa: number;
}

/**
 * Smooth partition of unity over the biome selector value. Band edges get
 * a ±0.06 crossfade so biome borders are slopes, not seams.
 */
export function bandWeights(band: number): BandWeights {
  const toPlains = smoothstep(-0.31, -0.19, band);
  const toRidge = smoothstep(-0.01, 0.11, band);
  const toMesa = smoothstep(0.24, 0.36, band);
  return {
    fen: 1 - toPlains,
    plains: toPlains * (1 - toRidge),
    ridgeland: toRidge * (1 - toMesa),
    mesa: toMesa,
  };
}

/** The biome selector noise at a point (unwarped: bands stay readable). */
export function bandValue(x: number, z: number): number {
  return fbm2(SEED_BAND, x * BAND_FREQ, z * BAND_FREQ, 3);
}

/** Terrain height in metres before the settlement mask. */
function rawHeightAt(x: number, z: number): number {
  const wx = x + valueNoise2(SEED_WARP_X, x * WARP_FREQ, z * WARP_FREQ) * WARP_AMPLITUDE;
  const wz = z + valueNoise2(SEED_WARP_Z, x * WARP_FREQ, z * WARP_FREQ) * WARP_AMPLITUDE;

  const base = fbm2(SEED_BASE, wx * BASE_FREQ, wz * BASE_FREQ, 5);
  const w = bandWeights(bandValue(x, z));

  let h = w.fen * fenHeight(base) + w.plains * plainsHeight(base) + w.mesa * mesaHeight(base);
  if (w.ridgeland > 0) {
    const ridge = ridgedFbm2(SEED_RIDGE, wx * RIDGE_FREQ, wz * RIDGE_FREQ, 4);
    h += w.ridgeland * ridgelandHeight(base, ridge);
  }
  return h;
}

/**
 * Ground elevation at any point on the planet. Pure and total: callers may
 * evaluate outside ±PLANET_HALF (chunk skirts at the world edge do), the
 * field simply continues.
 */
export function heightAt(x: number, z: number): number {
  const r = Math.sqrt(x * x + z * z);
  if (r >= SETTLEMENT_BLEND_RADIUS) return rawHeightAt(x, z);
  const mask = smoothstep(SETTLEMENT_FLAT_RADIUS, SETTLEMENT_BLEND_RADIUS, r);
  if (mask === 0) return SETTLEMENT_PLATEAU_Y;
  return SETTLEMENT_PLATEAU_Y + (rawHeightAt(x, z) - SETTLEMENT_PLATEAU_Y) * mask;
}

/**
 * Which biome a point belongs to. Inside the blend radius it is always
 * `settlement`; outside, the dominant band. Used for prop scatter, ambience
 * and (later) resource weighting — never for movement rules.
 */
export function biomeAt(x: number, z: number): Biome {
  if (x * x + z * z < SETTLEMENT_BLEND_RADIUS * SETTLEMENT_BLEND_RADIUS) return "settlement";
  const w = bandWeights(bandValue(x, z));
  let best: Biome = "plains";
  let bestW = w.plains;
  if (w.fen > bestW) {
    best = "fen";
    bestW = w.fen;
  }
  if (w.ridgeland > bestW) {
    best = "ridgeland";
    bestW = w.ridgeland;
  }
  if (w.mesa > bestW) best = "mesa";
  return best;
}
